import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Search, ShoppingBag } from 'lucide-react';
import { fetchProducts } from '../services/api';
import { toast } from 'sonner';
import ContactButton from '../components/ContactButton';

const Shop = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    loadProducts();
  }, []);

  useEffect(() => {
    filterProducts();
  }, [searchTerm, selectedCategory, products]);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const productsData = await fetchProducts();
      setProducts(productsData);
      setFilteredProducts(productsData);
    } catch (error) {
      console.error('Failed to load products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const filterProducts = () => {
    let filtered = products;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(product =>
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(product =>
        product.category?.toLowerCase() === selectedCategory.toLowerCase() ||
        product.tags?.some(tag => tag.toLowerCase() === selectedCategory.toLowerCase())
      );
    }

    setFilteredProducts(filtered);
  };

  const categories = ['all', ...new Set(products.flatMap(p => p.tags || []))].filter(Boolean);

  return (
    <div className="min-h-screen bg-cream overflow-x-hidden">
      {/* Header */}
      <header className="fixed top-0 w-full z-50 bg-charcoal/95 backdrop-blur-md border-b border-warmSage/20">
        <nav className="container mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <a href="/" className="flex items-center space-x-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_fashion-empire-2/artifacts/bcsdxd12_IMG_2797.PNG" 
                alt="PRSVR Logo" 
                className="h-10"
                style={{ filter: 'brightness(0) saturate(100%) invert(98%) sepia(6%) saturate(214%) hue-rotate(327deg) brightness(102%) contrast(96%)' }}
              />
            </a>
            <div className="flex items-center gap-2 sm:gap-4">
              <Button onClick={() => navigate('/')} variant="ghost" className="text-cream hover:text-warmSage text-sm sm:text-base">
                Home
              </Button>
              <Button onClick={() => navigate('/uniform-order')} className="bg-warmSage text-charcoal hover:bg-warmSage/90 text-sm sm:text-base">
                Uniforms
              </Button>
            </div>
          </div>
        </nav>
      </header>

      <div className="container mx-auto px-4 sm:px-6 pt-24 sm:pt-32 pb-16 max-w-full">
        {/* Page Title */}
        <div className="text-center mb-12">
          <Badge className="bg-charcoal text-cream mb-4">PRSVR COLLECTION</Badge>
          <h1 className="text-5xl md:text-6xl font-black text-charcoal uppercase mb-4 tracking-tight">Shop All</h1>
          <p className="text-lg md:text-xl text-warmGray font-medium">Performance-grade luxury. Built to PRSVR.</p>
        </div>

        {/* Search and Filter */}
        <div className="mb-12 space-y-6">
          <div className="relative max-w-xl mx-auto">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-warmGray h-5 w-5" />
            <Input
              type="text"
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-12 h-14 text-lg border-2 border-charcoal focus:border-warmSage"
            />
          </div>

          {/* Category Filters */}
          <div className="flex flex-wrap justify-center gap-3">
            {categories.slice(0, 8).map((category) => (
              <Button
                key={category}
                onClick={() => setSelectedCategory(category)}
                variant={selectedCategory === category ? 'default' : 'outline'}
                className={`${
                  selectedCategory === category
                    ? 'bg-charcoal text-cream'
                    : 'border-charcoal text-charcoal hover:bg-charcoal hover:text-cream'
                } font-bold uppercase`}
              >
                {category}
              </Button>
            ))}
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="text-center py-20">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-charcoal border-t-warmSage"></div>
            <p className="mt-4 text-warmGray">Loading products...</p>
          </div>
        )}

        {/* Products Grid */}
        {!loading && filteredProducts.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-8">
            {filteredProducts.map((product) => (
              <Card
                key={product.id}
                className="product-card-streetwear group cursor-pointer"
                onClick={() => navigate(`/products/${product.handle}`)}
              >
                <div className="relative overflow-hidden aspect-[4/5] bg-warmGray/10">
                  {product.image ? (
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-contain transition-transform duration-700 group-hover:scale-105"
                    />
                  ) : (
                    <div className="w-full h-full bg-warmGray/20 flex items-center justify-center">
                      <ShoppingBag className="h-20 w-20 text-warmGray" />
                    </div>
                  )}
                  <div className="product-overlay-streetwear">
                    <Button className="btn-primary-streetwear">VIEW PRODUCT</Button>
                  </div>
                  {product.category && (
                    <div className="product-badge-overlay">
                      <Badge className="badge-light">{product.category}</Badge>
                    </div>
                  )}
                </div>
                <CardContent className="p-6 bg-charcoal">
                  <h3 className="product-title-streetwear text-lg mb-2">{product.name}</h3>
                  <p className="product-desc-streetwear text-sm mb-3 line-clamp-2">
                    {product.description}
                  </p>
                  <div className="flex justify-between items-center">
                    <p className="product-price-streetwear text-2xl">${product.price.toFixed(2)}</p>
                    {product.tags && product.tags.includes('made-to-order') ? (
                      <Badge 
                        className="bg-blue-600 text-white font-bold cursor-pointer hover:bg-blue-700 transition-all whitespace-nowrap"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate('/pricing-policy');
                        }}
                        title="Click to learn about Made to Order items"
                      >
                        MADE TO ORDER ℹ️
                      </Badge>
                    ) : (
                      <Badge className={product.available ? 'bg-green-500' : 'bg-red-500'}>
                        {product.available ? 'In Stock' : 'Sold Out'}
                      </Badge>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* No Results */}
        {!loading && filteredProducts.length === 0 && (
          <div className="text-center py-20">
            <ShoppingBag className="h-20 w-20 text-warmGray mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-charcoal mb-2">No products found</h3>
            <p className="text-warmGray mb-6">Try adjusting your search or filters</p>
            <Button onClick={() => { setSearchTerm(''); setSelectedCategory('all'); }}>
              Clear Filters
            </Button>
          </div>
        )}
      </div>

      <ContactButton />
    </div>
  );
};

export default Shop;
